// frontend/src/pages/TopGrantsTab.js
import React, { useState, useEffect, useMemo } from 'react';
import { getGrantsForFaculty, getRelatedFacultyForGrant, getExternalLink } from '../../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  useTheme,
  Fade,
  Link,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TopGrantsModal from './modals/TopGrantsModal';
import debounce from 'lodash.debounce';
import { useAnalytics } from '../../hooks/useAnalytics';

function TopGrantsTab() {
  // State declarations
  const [grants, setGrants] = useState([]);
  const [totalGrants, setTotalGrants] = useState(0);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [relatedFaculty, setRelatedFaculty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const grantsPerPage = 10;

  // State for search query
  const [searchQuery, setSearchQuery] = useState('');

  const theme = useTheme();

  // Analytics hook
  const { 
    trackGrantViewed, 
    trackGrantSearched, 
    trackPaginationUsed, 
    trackGrantLinkClicked,
    trackError: trackErrorEvent
  } = useAnalytics();

  // State for controlling fade transition
  const [fadeIn, setFadeIn] = useState(false);

  // Create a debounced handler for search input (300ms delay)
  const debouncedSearch = useMemo(() =>
    debounce((value) => {
      if (value.trim()) {
        // Track search only if there's an actual query
        trackGrantSearched(value);
      }
      setSearchQuery(value);
      setCurrentPage(1);
    }, 300)
  , [trackGrantSearched]);

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Fetch grants whenever currentPage, or searchQuery changes.
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Track pagination usage when page changes
    if (currentPage > 1) {
      trackPaginationUsed(currentPage, 'grants');
    }
    
    getGrantsForFaculty(currentPage, grantsPerPage, searchQuery)
      .then((data) => {
        setGrants(data.grants);
        setTotalGrants(data.total);
        setLoading(false);
      })
      .catch((error) => {
        const errorMessage = 'Failed to fetch grants.';
        setError(errorMessage);
        setLoading(false);
        // Track the error
        trackErrorEvent(errorMessage, { 
          page: currentPage, 
          searchQuery,
          errorDetails: error?.message
        });
      });
  }, [currentPage, searchQuery, trackPaginationUsed, trackErrorEvent]);

  // Trigger the Fade transition when loading is complete.
  useEffect(() => {
    setFadeIn(!loading);
  }, [loading]);

  // Handler for clicking a grant row to open the modal
  const handleGrantRowClick = (grant) => {
    // Track that a grant was viewed - pass the full grant object for opportunity_id
    trackGrantViewed(grant.id, grant);
    
    getRelatedFacultyForGrant(grant.id)
      .then((data) => {
        setRelatedFaculty(data);
        setSelectedGrant(grant);
      })
      .catch((err) => {
        console.error('Failed to fetch related faculty:', err);
        trackErrorEvent('Failed to fetch related faculty', {
          grantId: grant.id,
          errorDetails: err?.message
        });
      });
  };

  // Handler for external link clicks
  const handleExternalLinkClick = (grantId, url) => {
    // Find the grant to get the opportunity_id
    const grant = grants.find(g => g.id === grantId);
    
    // Track the event using the analytics hook
    trackGrantLinkClicked(grantId, url, grant?.opportunity_id);
    
    // Prevent the default behavior to avoid multiple tabs
    // Only call the API endpoint to track on the backend and open the URL once
    getExternalLink(grantId)
      .then(data => {
        // Open the URL in a new tab - only once
        window.open(data.external_url || url, '_blank');
      })
      .catch(err => {
        console.error('Error tracking external link click:', err);
        // Fall back to the URL provided if the API call fails
        window.open(url, '_blank');
      });
  };

  // Handler to close the modal
  const handleCloseModal = () => {
    setSelectedGrant(null);
    setRelatedFaculty([]);
  };

  // Format funding string
  const formatFunding = (funding) => {
    if (!funding || funding.toLowerCase() === 'none - none') {
      return 'N/A';
    }
    const [min, max] = funding.split('-').map((value) => value.trim());
    const formattedMin = !isNaN(min) ? `$${Number(min).toLocaleString()}` : 'N/A';
    const formattedMax = !isNaN(max) ? `$${Number(max).toLocaleString()}` : 'N/A';
    if (formattedMin !== 'N/A' && formattedMax !== 'N/A') {
      return `${formattedMin} - ${formattedMax}`;
    } else if (formattedMin !== 'N/A') {
      return formattedMin;
    } else if (formattedMax !== 'N/A') {
      return formattedMax;
    }
    return 'N/A';
  };

  // Calculate total pages based on total grants returned by the API.
  const totalPages = Math.ceil(totalGrants / grantsPerPage);

  return (
    <Box>
      {/* Title Section */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          marginBottom: 1,
          color: theme.palette.text.primary,
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
        }}
      >
        Top Grants
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
        <InfoOutlinedIcon
          color="primary"
          sx={{
            marginRight: 1,
            fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' }
          }}
        />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}
        >
          Up-to-date grant data from grants.gov, personally recommended for you.<br />
          (These recommendations are based off of your Faculty Summary)
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ marginBottom: 2, ml: 2 }}>
        <TextField
          label="Search Grants"
          placeholder="Search Grants"
          variant="outlined"
          size="small"
          onChange={(e) => {
            debouncedSearch(e.target.value);
          }}
          inputProps={{
            maxLength: 100,
            'aria-label': 'Search for grants by name',
          }}
          sx={{ width: '50%' }}
        />
      </Box>

      {/* Loading/Error/Empty States */}
      {loading && currentPage === 1 && <Typography>Loading grants...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && grants.length === 0 && (
        <Typography>No grants found.</Typography>
      )}

      {/* Grants Table */}
      {!loading && !error && grants.length > 0 && (
        <Fade in={fadeIn} timeout={300}>
          <Box>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: 2,
                backgroundColor: 'white',
                boxShadow: theme.shadows[1],
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      Grant Name
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      Agency
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      Estimated Funding
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grants.map((grant) => (
                    <TableRow
                      key={grant.id}
                      onClick={() => handleGrantRowClick(grant)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1.05rem', color: theme.palette.text.primary }}>
                        <Link
                          component="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            // Track the link click for analytics
                            handleExternalLinkClick(grant.id, grant.link ?? `https://www.grants.gov/search-results-detail/${grant.opportunity_id}`);
                          }}
                          sx={{
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '1.05rem',
                            textAlign: 'left',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          aria-label={`View ${grant.name} grant details (opens in new window)`}
                        >
                          {grant.name}
                        </Link>
                      </TableCell>
                      <TableCell>{grant.agency_name}</TableCell>
                      <TableCell>{formatFunding(grant.estimated_funding)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 2,
              }}
            >
              {currentPage > 1 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    // Track pagination usage
                    trackPaginationUsed(newPage, 'grants');
                  }}
                  sx={{ marginRight: 1 }}
                >
                  Previous
                </Button>
              )}
              <Typography variant="body1" sx={{ marginX: 2 }}>
                Page {currentPage} of {totalPages}
              </Typography>
              {currentPage < totalPages && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    // Track pagination usage
                    trackPaginationUsed(newPage, 'grants');
                  }}
                  sx={{ marginLeft: 1 }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Grant Details Modal */}
      <TopGrantsModal
        selectedGrant={selectedGrant}
        relatedFaculty={relatedFaculty}
        onClose={handleCloseModal}
        onExternalLinkClick={handleExternalLinkClick}
      />
    </Box>
  );
}

export default TopGrantsTab;
